import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { JsonViewer } from '@/components/json-viewer'
import { HoldButton } from '@/components/hold-button'
import { ProviderMark } from '@/components/provider-mark'
import { useIsMobile } from '@/hooks/use-media-query'
import { t } from '@/lib/i18n'
import { formatDateTime } from '@/lib/time'
import type { WebhookEvent } from '@/lib/types'

/*
  EventInspector — the shared body of the event inspector: provider/type/status
  header, payload/headers/delivery tabs, replay, and metadata. Rendered two ways
  from one definition:

    - variant="page" : the full-screen /events/:id route. Split main + 320px
      metadata sidebar on wide screens, stacked on mobile.
    - variant="pane" : the inline Discover detail pane. Always the narrow stacked
      layout, since the pane is ~400px regardless of viewport.

  Owns its own tab and replay state. Replay always targets the current event
  (the mutation reads event.id), and the selected tab intentionally persists
  when the pane swaps events, so you can compare the same facet across a triage
  session.
*/
function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'err' | 'mono' | 'neutral'; children: React.ReactNode }) {
  const tones = {
    ok:      { bg: 'color-mix(in srgb, var(--green) 12%, transparent)',  fg: 'var(--green)' },
    warn:    { bg: 'color-mix(in srgb, var(--amber) 12%, transparent)',  fg: 'var(--amber)' },
    err:     { bg: 'color-mix(in srgb, var(--red) 12%, transparent)',    fg: 'var(--red)' },
    mono:    { bg: 'var(--surface-2)', fg: 'var(--muted)' },
    neutral: { bg: 'var(--surface-2)', fg: 'var(--muted)' },
  }
  const c = tones[tone] ?? tones.neutral
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 7px',
      borderRadius: 999,
      fontSize: 11, fontWeight: 500,
      background: c.bg, color: c.fg,
      border: `1px solid ${c.bg}`,
      fontFamily: tone === 'mono' ? 'var(--font-mono, ui-monospace, monospace)' : 'inherit',
      lineHeight: '16px',
    }}>{children}</span>
  )
}

export function EventInspector({ event, variant }: { event: WebhookEvent; variant: 'page' | 'pane' }) {
  const isMobile = useIsMobile()
  // The pane is a narrow fixed column, so it always uses the stacked layout;
  // the full page only stacks once the viewport itself is narrow.
  const stacked = variant === 'pane' || isMobile
  const [tab, setTab] = useState<'payload' | 'headers' | 'delivery'>('payload')
  const queryClient = useQueryClient()

  const replay = useMutation({
    mutationFn: () => api.post(`/api/events/${event.id}/replay`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['events'] }),
  })

  const sigValid = event.signature_valid
  const respOk = event.response_status != null && event.response_status >= 200 && event.response_status < 300
  const respErr = event.response_status != null && event.response_status >= 400
  const headerEntries = Object.entries(event.headers || {})

  return (
    <div style={{
      flex: 1, minHeight: 0,
      overflow: stacked ? 'auto' : 'hidden',
      display: 'grid',
      gridTemplateColumns: stacked ? '1fr' : '1fr 320px',
    }}>
      {/* Main pane */}
      <div style={{
        overflow: stacked ? 'visible' : 'auto',
        borderRight: stacked ? 'none' : '1px solid var(--border)',
        minWidth: 0,
      }}>
        {/* Event header — title/meta on the left, the single Replay control on the right */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <ProviderMark name={event.provider} size={22} />
            <h1 style={{
              margin: 0,
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
              fontSize: variant === 'pane' ? 15 : 17, fontWeight: 500,
              letterSpacing: '-0.005em',
              color: 'var(--white)',
              overflowWrap: 'anywhere', minWidth: 0,
            }}>{event.event_type || event.method || 'event'}</h1>
            {event.response_status != null && (
              <Pill tone={respOk ? 'ok' : respErr ? 'err' : 'warn'}>{event.response_status}</Pill>
            )}
            {sigValid != null && (
              <Pill tone={sigValid ? 'ok' : 'err'}>
                {sigValid ? t('signatureVerified') : t('signatureInvalid')}
              </Pill>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 12, color: 'var(--muted)' }}>
            <span style={{
              fontFamily: 'var(--font-mono, ui-monospace, monospace)',
              overflowWrap: 'anywhere', wordBreak: 'break-all', minWidth: 0,
            }}>{event.id}</span>
            <span aria-hidden="true">·</span>
            <span>{t('received')} {formatDateTime(event.received_at)}</span>
            {event.response_time_ms != null && (
              <>
                <span aria-hidden="true">·</span>
                <span>
                  {t('forwarded')}{' '}
                  <span style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', color: 'var(--text)' }}>
                    {event.response_time_ms}ms
                  </span>
                </span>
              </>
            )}
            {event.body_size != null && (
              <>
                <span aria-hidden="true">·</span>
                <span style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', color: 'var(--text)' }}>
                  {(event.body_size / 1024).toFixed(1)} KB
                </span>
              </>
            )}
          </div>
          </div>
          <HoldButton
            onAction={() => replay.mutateAsync()}
            disabled={replay.isPending}
            errorLabel={t('replayFailed')}
            style={{ flexShrink: 0 }}
          >
            {t('holdToReplay') || 'Hold to replay'}
          </HoldButton>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex', gap: 0,
          borderBottom: '1px solid var(--border)',
          padding: '0 20px',
          backgroundColor: 'var(--surface)',
          overflowX: 'auto',
        }}>
          {(['payload', 'headers', 'delivery'] as const).map(name => (
            <button
              key={name}
              type="button"
              onClick={() => setTab(name)}
              className="tap"
              style={{
                padding: '10px 14px',
                border: 'none',
                background: 'transparent',
                borderBottom: `2px solid ${tab === name ? 'var(--white)' : 'transparent'}`,
                color: tab === name ? 'var(--white)' : 'var(--muted)',
                fontWeight: tab === name ? 500 : 400,
                fontSize: 12.5,
                cursor: 'pointer',
                marginBottom: -1,
                textTransform: 'capitalize',
                fontFamily: 'inherit',
                flexShrink: 0,
              }}
            >
              {t(name)}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {tab === 'payload' && (
          <div style={{ padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <Pill tone="mono">JSON</Pill>
            </div>
            <JsonViewer data={typeof event.body === 'string' ? event.body : JSON.stringify(event.body)} />
          </div>
        )}

        {tab === 'headers' && (
          <div style={{ padding: 20 }}>
            {headerEntries.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 12 }}>{t('noHeaders')}</div>
            ) : (
              <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', backgroundColor: 'var(--surface)' }}>
                {headerEntries.map(([k, v], i) => (
                  <div
                    key={k}
                    style={{
                      display: stacked ? 'flex' : 'grid',
                      flexDirection: stacked ? 'column' : undefined,
                      gap: stacked ? 2 : undefined,
                      gridTemplateColumns: stacked ? undefined : '200px 1fr',
                      padding: '10px 14px',
                      borderBottom: i < headerEntries.length - 1 ? '1px solid var(--border)' : 'none',
                      fontSize: 12,
                      fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                    }}
                  >
                    <span style={{ color: 'var(--muted)', wordBreak: 'break-all' }}>{k}</span>
                    <span style={{ color: 'var(--text)', wordBreak: 'break-all' }}>{v}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'delivery' && <DeliveryTab event={event} />}
      </div>

      {/* Metadata — right sidebar when split, stacked section below otherwise.
          Replay now lives in the header (a single, prominent control). */}
      <aside style={{
        overflow: stacked ? 'visible' : 'auto',
        backgroundColor: 'var(--surface)',
        borderTop: stacked ? '1px solid var(--border)' : 'none',
      }}>
        <SidePanel title={t('metadata')}>
          <MetaRow label={t('method')} value={event.method} mono />
          <MetaRow label={t('provider')} value={event.provider || '—'} />
          <MetaRow label={t('source')} value={event.source_ip} mono />
          <MetaRow label={t('size')} value={event.body_size != null ? `${event.body_size} B` : '—'} mono />
          <MetaRow
            label={t('attempts')}
            value={String(event.delivery_attempts)}
            mono
            valueColor={event.delivery_attempts > 1 ? 'var(--amber)' : undefined}
          />
          {event.last_error && (
            <MetaRow label={t('error')} value={event.last_error} valueColor="var(--red)" />
          )}
        </SidePanel>
      </aside>
    </div>
  )
}

function SidePanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: '16px 16px', borderBottom: '1px solid var(--border)' }}>
      <div style={{
        fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.06em',
        color: 'var(--muted)', fontWeight: 500, marginBottom: 10,
      }}>{title}</div>
      {children}
    </div>
  )
}

function MetaRow({ label, value, mono, valueColor }: {
  label: string; value: string; mono?: boolean; valueColor?: string
}) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, padding: '6px 0', fontSize: 12 }}>
      <span style={{ color: 'var(--muted)', flexShrink: 0 }}>{label}</span>
      <span style={{
        color: valueColor || 'var(--text)',
        fontFamily: mono ? 'var(--font-mono, ui-monospace, monospace)' : 'inherit',
        textAlign: 'right', wordBreak: 'break-all', minWidth: 0,
      }}>{value}</span>
    </div>
  )
}

// DeliveryTab surfaces the forward outcome: the target's HTTP status code
// (colored), the round-trip latency, and — only when a delivery failed — the
// captured response body (the real error message). A network failure (no HTTP
// response) shows the status as "—" plus a note.
function DeliveryTab({ event }: { event: WebhookEvent }) {
  const status = event.response_status
  const totalMs = event.response_time_ms
  const respBody = event.response_body
  // Network failure: no HTTP status but an error was recorded.
  const networkError = status == null && !!event.last_error

  if (totalMs == null && status == null && !networkError) {
    return <div style={{ padding: 20, color: 'var(--muted)', fontSize: 13 }}>{t('noDeliveryData')}</div>
  }

  const statusColor =
    status == null ? 'var(--muted)'
    : status < 300 ? 'var(--green, #2ecc71)'
    : status < 400 ? 'var(--amber, #d98a00)'
    : 'var(--red)'

  return (
    <div style={{ padding: 20 }}>
      {/* Status code */}
      <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{t('statusCodeLabel')}</div>
      <div style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 26, fontWeight: 500, letterSpacing: '-0.02em', color: statusColor }}>
        {status != null ? status : '—'}
      </div>
      {networkError && (
        <div style={{ marginTop: 8, fontSize: 13, color: 'var(--muted)' }}>{t('noHttpResponse')}</div>
      )}

      {/* Latency */}
      {totalMs != null && (
        <>
          <div style={{ fontSize: 12, color: 'var(--muted)', margin: '18px 0 8px' }}>{t('totalDeliveryTime')}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 22, fontWeight: 500, color: 'var(--text)' }}>{totalMs.toLocaleString()}</span>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>ms</span>
          </div>
        </>
      )}

      {/* Response body — only on error (non-empty) */}
      {respBody && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>
            {t('responseLabel')}
            {event.response_truncated && <span style={{ marginLeft: 8, color: 'var(--amber, #d98a00)', fontSize: 11 }}>({t('responseTruncated')})</span>}
          </div>
          <pre style={{
            margin: 0, padding: 12, borderRadius: 6, overflow: 'auto', maxHeight: 280,
            backgroundColor: 'var(--surface-2)', color: 'var(--text)',
            fontFamily: 'var(--font-mono, ui-monospace, monospace)', fontSize: 12, lineHeight: 1.5,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          }}>{respBody}</pre>
        </div>
      )}
    </div>
  )
}
